import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { isElevatedRole, type AppViewMode } from '@/lib/appAccess';
import { canAccessAppPath } from '@/lib/appAccess';
import { getMyAppAccess, type EffectiveAccess } from '@/api/appAccess';
import { configuredPathAccess } from '@/lib/configuredAccess';

const VIEW_MODE_STORAGE_KEY = 'compliance-magic:view-mode';

type AppViewContextType = {
  viewMode: AppViewMode;
  canPreviewViews: boolean;
  isStandardPreview: boolean;
  setViewMode: (viewMode: AppViewMode) => void;
  canAccessPath: (pathname: string) => boolean;
  hasAccess: (key: string) => boolean;
  accessLoading: boolean;
  accessError: boolean;
  refreshAccess: () => Promise<void>;
};

const AppViewContext = createContext<AppViewContextType | undefined>(undefined);

function readStoredViewMode(): AppViewMode {
  if (typeof window === 'undefined') return 'elevated';
  return window.sessionStorage.getItem(VIEW_MODE_STORAGE_KEY) === 'standard'
    ? 'standard'
    : 'elevated';
}

export function AppViewProvider({ children }: { children: ReactNode }) {
  const { user, profile, loading } = useAuth();
  const [requestedViewMode, setRequestedViewMode] = useState<AppViewMode>(readStoredViewMode);
  const [accessState, setAccessState] = useState<{ owner: string; access: EffectiveAccess } | null>(null);
  const [accessLoading, setAccessLoading] = useState(true);
  const [accessError, setAccessError] = useState(false);
  const owner = `${user?.id ?? ''}:${profile?.role ?? ''}`;
  const access = accessState?.owner === owner ? accessState.access : null;
  const canPreviewViews = isElevatedRole(profile?.role) && profile?.is_active !== false && (access?.is_admin ?? false);
  const viewMode: AppViewMode = canPreviewViews ? requestedViewMode : 'standard';

  useEffect(() => {
    let current = true;
    let sequence = 0;
    const refresh = async () => {
      if (!user || loading) return;
      const request = ++sequence;
      try {
        const access = await getMyAppAccess();
        if (current && request === sequence) {
          setAccessState({ owner, access });
          setAccessError(false);
        }
      } catch {
        if (current && request === sequence) { setAccessState(null); setAccessError(true); }
      } finally {
        if (current && request === sequence) setAccessLoading(false);
      }
    };
    void refresh();
    const onRefresh = () => { void refresh(); };
    window.addEventListener('focus', onRefresh);
    window.addEventListener('app-access-changed', onRefresh);
    const interval = window.setInterval(onRefresh, 60_000);
    return () => {
      current = false;
      window.clearInterval(interval);
      window.removeEventListener('focus', onRefresh);
      window.removeEventListener('app-access-changed', onRefresh);
    };
  }, [user?.id, owner, loading]);

  const refreshAccess = useCallback(async () => {
    window.dispatchEvent(new Event('app-access-changed'));
  }, []);
  const canAccessPath = useCallback((pathname: string) => {
    // Preview is a presentation mode, never an authorization claim.
    if (!access) return false;
    const path = pathname.toLowerCase().replace(/\/+$/, '');
    if ((path === '/atbs/prompts' || path.startsWith('/atbs/prompts/')) && profile?.role !== 'admin_level_3') return false;
    if ((path === '/atbs/insights' || path.startsWith('/atbs/insights/') || path === '/customer-signals' || path.startsWith('/customer-signals/')) && profile?.role !== 'admin_level_3') return false;
    if (canPreviewViews) return canAccessAppPath(viewMode, pathname);
    return access ? configuredPathAccess(access, pathname) : false;
  }, [access, canPreviewViews, viewMode, profile?.role]);
  const hasAccess = useCallback((key: string) => Boolean(access?.allowed[key]), [access]);

  useEffect(() => {
    if (loading || !access) return;

    if (!user || !canPreviewViews) {
      window.sessionStorage.removeItem(VIEW_MODE_STORAGE_KEY);
      setRequestedViewMode('elevated');
    }
  }, [canPreviewViews, loading, user, access]);

  const setViewMode = useCallback((nextViewMode: AppViewMode) => {
    if (!canPreviewViews) return;

    setRequestedViewMode(nextViewMode);
    window.sessionStorage.setItem(VIEW_MODE_STORAGE_KEY, nextViewMode);
  }, [canPreviewViews]);

  const value = useMemo<AppViewContextType>(() => ({
    viewMode,
    canPreviewViews,
    isStandardPreview: canPreviewViews && viewMode === 'standard',
    setViewMode,
    canAccessPath,
    hasAccess,
    accessLoading: accessLoading || (!access && !accessError),
    accessError,
    refreshAccess,
  }), [canPreviewViews, setViewMode, viewMode, canAccessPath, hasAccess, accessLoading, accessError, access, refreshAccess]);

  return <AppViewContext.Provider value={value}>{children}</AppViewContext.Provider>;
}

export function useAppView() {
  const context = useContext(AppViewContext);
  if (!context) {
    throw new Error('useAppView must be used within an AppViewProvider');
  }
  return context;
}
